import React, { useEffect, useState } from "react";

import type {
    LinksFunction,
    LoaderFunction,
    MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import type { ShouldRevalidateFunctionArgs } from "@remix-run/react";
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLoaderData,
    useRouteError,
    useRouteLoaderData,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
    dehydrate,
    HydrationBoundary,
    QueryClient,
    QueryClientProvider,
    hydrate,
} from "@tanstack/react-query";
import { SpeedInsights } from "@vercel/speed-insights/remix";
// eslint-disable-next-line import/no-duplicates
import sonnerStylesInline from "sonner/dist/styles.css?inline";
// eslint-disable-next-line import/no-duplicates
import sonnerStylesUrl from "sonner/dist/styles.css?url";
import { useDehydratedState } from "~/hooks/use-dehydrated-state";

import { AnalyticsScreenTracker } from "~/components/analytics-screen-tracker";
import { AppUpdatePrompt } from "~/components/app-update-prompt";
import { GoogleTagManager } from "~/components/google-tag-manager";
import { HydrationErrorMonitor } from "~/components/hydration-error-monitor";
import { InstallAppPrompt } from "~/components/install-app-prompt";
import { PwaLaunchTracker } from "~/components/pwa-launch-tracker";
import { PwaTitleNormalizer } from "~/components/pwa-title-normalizer";
import { PolicyBanner } from "~/components/site-policy-banner";
import { HashLinkRouter } from "~/components/hash-link-router";
import { ReadOnlyModeBanner } from "~/components/read-only-mode-banner";
import { ConfirmDialogProvider } from "~/components/ui/confirm";
import { LogoutOverlayProvider } from "~/context/LogoutOverlayContext";
import { Toaster as Sonner } from "~/components/ui/sonner";
import { Toaster } from "~/components/ui/toaster";
import { TooltipProvider } from "~/components/ui/tooltip";
import { getOptionalAuth } from "~/config/asyncStore.server";
import { AnalyticsProvider } from "~/context/AnalyticsContext";
import { LoaderProvider } from "~/context/LoaderContext";
// import { ScreenSizeProvider } from "~/context/ScreenSizeContext";
import { SupabaseSessionProvider } from "~/context/SupabaseSessionContext";
// import { UserAgentContext } from "~/context/UserAgentContext";
import { TourProvider, TourViewport } from "~/features/onboarding";
import {
    DEFAULT_PRACTITIONER_ONBOARDING_STEP,
    isOnboardingPathAllowed,
    onboardingPathForStep,
} from "~/features/practitioner-onboarding/types";
import { helpCenterTourConfig } from "~/features/onboarding-checklist/config/help-center-tour";
import { consultCalloutTourConfig } from "~/features/onboarding-checklist/config/consult-tour";
import { consultantChatCalloutTourConfig } from "~/features/onboarding-checklist/config/consultant-chat-tour";
import {
    dpmpPartDetailBasicTourConfig,
    dpmpPartDetailTourConfig,
    dpmpPartsExileTourConfig,
    dpmpPartsTourConfig,
    dpmpPartsTourWithExileConfig,
    partsDpmpMergedTourConfig,
    partsDpmpMergedWithExileTourConfig,
} from "~/features/packages/config/dpmp-tour";
import { partsTourConfig } from "~/features/parts/config/parts-tour";
import { sessionTourConfig } from "~/features/sessions/config/session-tour";
import { clinicalNoteTourConfig } from "~/features/practitioner-feedback/config/clinical-note-tour";
import { consultationTourConfig } from "~/features/practitioner-feedback/config/consultation-tour";
import {
    consultantNavTourConfig,
    consultantChatTourConfig,
} from "~/features/practitioner-chat/config/consultant-tour";
import { SurveyProvider } from "~/features/surveys";
import {
    getReadOnlyModeState,
    toPublicReadOnlyModeState,
} from "~/modules.server/read-only-mode.server";
import type { ScreenSize } from "~/hooks/use-screen-size";
import { safeReload } from "~/lib/app-update";
import { isSentryEnabled } from "~/lib/env.shared";
import { getBrowserEnv } from "~/utils.server/env";

import { withCors } from "./config/cors";
import { buildAttributionCookie } from "./modules.server/profile/attribution-cookie.server";
import { PageViewTracker } from "./components/page-view-tracker";
import { UserAgentContext } from "./context/UserAgentContext";
import type { UserDetailsData } from "./modules.server/profile/user-details-service";
import { prefetchUserDetails } from "./modules.server/profile/user-details-service";
import { prefetchEligibleTriggers } from "./features/surveys/server/eligible-triggers-service";
// AIDEV-NOTE: Dynamic import intentional — billing/index.ts has top-level side effects
// (singleton instantiation) that prevent Vite from tree-shaking it out of the client bundle
// when imported statically. This keeps billing → logger → store.server out of the client build.
import tailwindStyles from "./index.css?url";
import { parseUserAgent } from "./utils.server/user-agent";

export const links: LinksFunction = () => [
    { rel: "stylesheet", href: tailwindStyles },
    { rel: "stylesheet", href: sonnerStylesUrl },
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap",
    },
    { rel: "manifest", href: "/manifest.webmanifest" },
    // AIDEV-NOTE: mirrors partscompanion.org (green mark in light mode, cream in dark); /favicon.ico is the fallback.
    {
        rel: "icon",
        type: "image/png",
        href: "/favicon-light.png",
        media: "(prefers-color-scheme: light)",
    },
    {
        rel: "icon",
        type: "image/png",
        href: "/favicon-dark.png",
        media: "(prefers-color-scheme: dark)",
    },
    { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
];

export const meta: MetaFunction = () => [
    // { title: "Parts Companion" },
    {
        name: "description",
        content: "Clarity, continuity, and connection in your IFS journey.",
    },
    {
        name: "viewport",
        content:
            "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5, viewport-fit=cover",
    },
    // AIDEV-NOTE: PWA meta tags for iOS home screen support — standalone mode,
    // beige status bar matching the app background, and theme-color for the
    // area behind the Dynamic Island / notch.
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "default" },
    { name: "theme-color", content: "#FAF7F2" },
];

// Helper to map numeric viewport width to Tailwind-like breakpoints
const mapWidthToScreenSize = (width: number | undefined): string => {
    if (!width || Number.isNaN(width)) return "xs";

    if (width >= 1536) return "2xl";
    if (width >= 1280) return "xl";
    if (width >= 1024) return "lg";
    if (width >= 768) return "md";
    if (width >= 640) return "sm";
    return "xs";
};

export const loader: LoaderFunction = withCors(async ({ request }) => {
    const queryClient = new QueryClient();
    const auth = getOptionalAuth();
    const readOnlyModePromise = getReadOnlyModeState();

    // Prefetch the supabase session using the same query key and function as SupabaseSessionContext
    const sessionPrefetch = queryClient.prefetchQuery({
        queryKey: ["supabase-session"],
        queryFn: async () => auth?.session ?? null,
    });

    const relationshipsPrefetch = prefetchUserDetails(queryClient, auth!);
    const eligibleTriggersPrefetch = prefetchEligibleTriggers(
        queryClient,
        auth!,
    );
    // AIDEV-NOTE: Dynamic import kept after the `show-payments-ui` removal — it is not a
    // circular-dependency workaround, it keeps the billing module (and the Stripe SDK it
    // pulls in) out of the root loader's static graph. Every authenticated request now
    // prefetches; there is no flag to skip it.
    const billingEntitlementPrefetch =
        auth?.authStatus === "authenticated"
            ? import("./modules.server/billing")
                  .then(({ prefetchBillingEntitlement }) =>
                      prefetchBillingEntitlement(queryClient, auth!),
                  )
                  .catch(() => undefined)
            : Promise.resolve();

    const [readOnlyMode] = await Promise.all([
        readOnlyModePromise,
        sessionPrefetch,
        relationshipsPrefetch,
        eligibleTriggersPrefetch,
        billingEntitlementPrefetch,
    ]);

    const userDetails = queryClient.getQueryData<UserDetailsData>(["user"]);
    if (
        auth?.authStatus === "authenticated" &&
        userDetails?.isPractitioner &&
        userDetails.featureFlags["enable-practitioner-onboarding-v2"] ===
            true &&
        userDetails.profile.terms_accepted_at &&
        userDetails.profile.onboarding_status !== "completed"
    ) {
        const url = new URL(request.url);
        const step =
            userDetails.practitionerInitialOnboarding?.step ??
            DEFAULT_PRACTITIONER_ONBOARDING_STEP;
        if (
            !isOnboardingPathAllowed({
                step,
                pathname: url.pathname,
                search: url.search,
            })
        ) {
            throw redirect(onboardingPathForStep(step));
        }
    }

    // AIDEV-NOTE: Parse sidebar-collapsed cookie for SSR — avoids layout flash on page load.
    const cookieHeader = request.headers.get("cookie") || "";
    const sidebarCollapsed = cookieHeader.includes("sidebar-collapsed=true");

    const userAgentString = request.headers.get("user-agent");
    const userAgent = parseUserAgent(userAgentString);

    // Try to read Client-Hint headers sent by the browser for viewport width
    const viewportWidthHeader =
        request.headers.get("sec-ch-viewport-width") ??
        request.headers.get("viewport-width");

    const initialScreenSize = mapWidthToScreenSize(
        viewportWidthHeader ? Number(viewportWidthHeader) : undefined,
    ) as ScreenSize;

    // AIDEV-NOTE: In Playwright test mode, read __test_layout cookie so tests can request
    // mobile or desktop layout. Defaults to "desktop". Undefined in production (no dual-render skip).
    let testLayoutOverride: "mobile" | "desktop" | undefined;
    if (process.env.PLAYWRIGHT_TEST === "1") {
        const cookieHeader = request.headers.get("cookie") || "";
        const match = cookieHeader.match(/__test_layout=(mobile|desktop)/);
        testLayoutOverride = (match?.[1] as "mobile" | "desktop") ?? "desktop";
    }

    // AIDEV-NOTE: First-party attribution cookie. The root loader runs on every
    // document request, so a tagged arrival on ANY page (not just the three
    // routes with client-side capture) persists utm_*/fbclid/gclid/rdt_cid for
    // 30 days. Signup, /api/attribution, and auth.callback resolve this cookie
    // as the fallback when sessionStorage is empty (new tab / later session).
    const attributionCookie = buildAttributionCookie(request);

    const headers = new Headers({
        // Ask browsers that support Client-Hints to send viewport width
        "Accept-CH": "Width, Viewport-Width",
        "Critical-CH": "Width, Viewport-Width",
    });
    if (attributionCookie) {
        headers.append("Set-Cookie", attributionCookie);
    }

    const response = json(
        {
            env: getBrowserEnv(),
            dehydratedState: dehydrate(queryClient),
            isAuthenticated: auth?.authStatus === "authenticated",
            userAgent,
            initialScreenSize,
            sidebarCollapsed,
            testLayoutOverride,
            readOnlyMode: toPublicReadOnlyModeState(readOnlyMode),
        },
        { headers },
    );
    return response;
});

// AIDEV-NOTE: Prevent loader revalidation on fetcher/action submissions
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    actionStatus,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
    // AIDEV-NOTE: A blocked mutation refreshes the root Redis state so tabs
    // opened before maintenance immediately receive the read-only banner.
    if (actionStatus === 503) return true;

    const isOnboardingNavigation =
        currentUrl.pathname.startsWith("/onboarding/") ||
        nextUrl.pathname.startsWith("/onboarding/") ||
        currentUrl.searchParams.get("onboarding") === "1" ||
        nextUrl.searchParams.get("onboarding") === "1";
    // AIDEV-NOTE: Onboarding advances optimistically; its ordered background
    // save must not be raced by a root-loader redirect using the previous step.
    // Fresh document visits still run the root loader and enforce server state.
    if (isOnboardingNavigation) return false;

    // Only revalidate on actual navigation (URL changes)
    if (currentUrl.pathname === nextUrl.pathname) {
        return false;
    }

    // Let default behavior handle param changes and navigations
    return defaultShouldRevalidate;
}

// AIDEV-NOTE: Error signatures of a stale client navigating after a deploy:
// code-split chunk hashes rotated away (per-browser dynamic-import messages)
// or single-fetch loader data that no longer decodes. These are not app bugs —
// the fix is always "reload onto the fresh build".
const STALE_BUILD_ERROR_PATTERNS = [
    "Failed to fetch dynamically imported module", // Chrome
    "error loading dynamically imported module", // Firefox
    "Importing a module script failed", // Safari
    "ChunkLoadError",
    "Unable to decode turbo-stream response", // single-fetch version skew
];

// AIDEV-NOTE: Quiet recovery splash for stale-build navigation errors. Rendered
// instead of "Something went wrong" while safeReload() takes us onto the new
// build (it no-ops during recordings/uploads and debounces reload loops — in
// those cases the manual button remains). See lib/app-update.ts.
function StaleBuildRecovery() {
    useEffect(() => {
        safeReload();
    }, []);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-bl from-brand-forest-700 to-brand-forest-900">
            <div className="flex flex-col items-center gap-4 text-center">
                <img
                    src="/parts-companion-logo-appicon-light.png"
                    alt="Parts Companion"
                    className="h-12 w-12"
                />
                <p className="text-white/80">Updating to the latest version…</p>
                <button
                    onClick={() => window.location.reload()}
                    className="text-sm text-white/60 underline hover:text-white/80"
                >
                    Reload now
                </button>
            </div>
        </div>
    );
}

// AIDEV-NOTE: The app hydrates with hydrateRoot(document), so the root ErrorBoundary
// replaces the whole <html>. It MUST render a full document (head/Links/Meta/body/Scripts),
// or document.body becomes null, CSS vanishes, and GTM/gtag throw (BACKEND-6BY/683).
// Deliberately omits GTM/analytics/SpeedInsights so error pages never double-count views.
// Root loader data is absent when the root loader itself threw — guard every read.
function RootErrorDocument({ children }: { children: React.ReactNode }) {
    const rootData = useRouteLoaderData("root") as
        | { env?: Record<string, string | undefined> }
        | undefined;
    const env = rootData?.env;

    return (
        <html lang="en" dir="ltr" className="h-screen-safe">
            <head suppressHydrationWarning>
                <meta charSet="utf-8" />
                <Links />
                <Meta />
            </head>
            <body className="h-screen-safe">
                {children}
                <div suppressHydrationWarning>
                    <Scripts />
                    {env && (
                        <script
                            dangerouslySetInnerHTML={{
                                __html: `window.env = ${JSON.stringify(env)}`,
                            }}
                        />
                    )}
                </div>
            </body>
        </html>
    );
}

export const ErrorBoundary = () => (
    <RootErrorDocument>
        <RootErrorContent />
    </RootErrorDocument>
);

function RootErrorContent() {
    const error = useRouteError();

    // AIDEV-NOTE: Enhanced error handling for Promise-related SSR errors
    const errorMessage = error instanceof Error ? error.message : String(error);

    // AIDEV-NOTE: Stale-build navigation (deploy happened, user didn't accept
    // the update toast, then clicked a nav item). Show the update splash and
    // reload instead of flashing "Something went wrong" — and skip the Sentry
    // capture below (expected deploy-time noise, not an app bug). Production
    // only — in dev these errors are real and should surface with a stack.
    if (
        process.env.NODE_ENV === "production" &&
        STALE_BUILD_ERROR_PATTERNS.some((pattern) =>
            errorMessage.includes(pattern),
        )
    ) {
        return <StaleBuildRecovery />;
    }

    // AIDEV-NOTE: SSR errors hydrate with no stack (Remix strips it) and are already
    // captured server-side in handleError — skip them now that the error page hydrates.
    const isHydratedServerError = error instanceof Error && !error.stack;
    if (isSentryEnabled() && !isHydratedServerError)
        captureRemixErrorBoundaryError(error);

    // Check if it's a Promise-related React error (common SSR issue)
    if (
        errorMessage.includes("Objects are not valid as a React child") &&
        errorMessage.includes("[object Promise]")
    ) {
        // Return a client-side only recovery component
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-amber-600">
                        Loading Error
                    </h1>
                    <p className="mt-2 text-gray-600">
                        The page encountered a loading error. Refreshing...
                    </p>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                            // Auto-refresh once to recover from SSR error
                            if (!sessionStorage.getItem('error_reload_attempted')) {
                                sessionStorage.setItem('error_reload_attempted', 'true');
                                setTimeout(() => window.location.reload(), 500);
                            }
                        `,
                        }}
                    />
                </div>
            </div>
        );
    }

    // Check if it's a Supabase initialization error
    if (
        errorMessage.includes("Supabase") ||
        errorMessage.includes("SUPABASE")
    ) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600">
                        Database Connection Error
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Unable to connect to the database. Please try again
                        later.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // In development, show a detailed error with stack trace for easier debugging
    if (process.env.NODE_ENV === "development") {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-2xl rounded-lg bg-red-50 p-6 shadow">
                    <h1 className="text-2xl font-bold text-red-700">
                        Application Error
                    </h1>
                    <p className="mt-2 break-all font-mono text-sm text-red-800">
                        {errorMessage}
                    </p>
                    {error instanceof Error && error.stack && (
                        <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-red-100 p-4 text-xs text-red-900">
                            {error.stack}
                        </pre>
                    )}
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    >
                        Reload
                    </button>
                </div>
            </div>
        );
    }

    return <div>Something went wrong</div>;
}

export default function App() {
    const loaderData = useLoaderData<typeof loader>();
    const { env, userAgent, readOnlyMode } = loaderData;
    // AIDEV-NOTE: useDehydratedState must be called conditionally to avoid SSR hook errors
    const dehydratedState = useDehydratedState();
    const [queryClient] = useState(() => {
        const qc = new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: 60 * 1000,
                    // Prevent refetches during hydration
                    refetchOnWindowFocus: false,
                    refetchOnMount: false,
                },
            },
        });

        // If we received dehydrated state from the server we can hydrate the
        // freshly-created client so that queries are immediately available.
        if (dehydratedState) {
            try {
                hydrate(qc, dehydratedState);
            } catch {}
        }

        return qc;
    });

    // AIDEV-NOTE: Register the minimal service worker (public/sw.js). Chrome only fires
    // `beforeinstallprompt` (which powers tap-to-install in install-app-prompt.tsx) when a
    // SW with a fetch handler is registered. The SW is a transparent passthrough — no caching
    // of HTML/PHI. iOS Safari ignores this and uses its own manual instruction sheet.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!("serviceWorker" in navigator)) return;
        // AIDEV-NOTE: Always force an update check. A previously-installed SW can
        // be broken in ways that stop pages loading (an older build intercepted
        // navigations and failed redirect-terminated ones), and a browser stuck
        // on it may never load a page cleanly enough to pick up the fix — the
        // bad SW blocks the very navigation that would replace it. update()
        // fetches /sw.js directly, out of band, so a stale worker always gets
        // superseded; sw.js uses skipWaiting + clients.claim so the new one
        // takes over immediately.
        navigator.serviceWorker
            .register("/sw.js")
            .then((registration) => registration.update())
            .catch(() => {
                /* registration failure is non-fatal — install prompt just won't appear */
            });
    }, []);

    const tourConfigs = React.useMemo(
        () => [
            partsTourConfig,
            sessionTourConfig,
            consultationTourConfig,
            clinicalNoteTourConfig,
            consultantNavTourConfig,
            consultantChatTourConfig,
            helpCenterTourConfig,
            consultCalloutTourConfig,
            consultantChatCalloutTourConfig,
            dpmpPartDetailTourConfig,
            dpmpPartDetailBasicTourConfig,
            dpmpPartsTourConfig,
            dpmpPartsTourWithExileConfig,
            dpmpPartsExileTourConfig,
            partsDpmpMergedTourConfig,
            partsDpmpMergedWithExileTourConfig,
        ],
        [],
    );

    return (
        <html lang="en" dir="ltr" className="h-screen-safe">
            {/*
             * We deliberately suppress hydration warnings for the entire
             * <head> element.  During development Vite (and some
             * third-party libraries) may inject additional <style> tags
             * into <head> *before* React gets the chance to run its
             * hydration pass.  Because the server-rendered HTML obviously
             * contains no such tags, React detects a mismatch and logs
             * recoverable hydration errors for the affected siblings
             * (<title>, <meta>, <link>, …).  The actual markup is still
             * perfectly fine – React just needs to be told that it is
             * allowed to reconcile minor differences.
             *
             * Adding `suppressHydrationWarning` at the <head> level keeps
             * the console clean without hiding *real* hydration problems
             * in the application body.
             */}
            <head suppressHydrationWarning>
                {/* AIDEV-NOTE: charSet must be first in <head> to prevent Windows browsers from decoding SSR HTML as CP1252, which baked UTF-8 mojibake into practitioner session notes for non-ASCII users. */}
                <meta charSet="utf-8" />
                <Links />
                <Meta />
                {/*
                 * Always include the inline Sonner stylesheet so that the
                 * DOM structure of <head> is identical on the server and
                 * on the client during the initial hydration pass.
                 *
                 * Rendering it conditionally only on the server caused a
                 * mismatch (the server HTML contained the <style> element
                 * while the client render did not), which in turn broke
                 * React's hydration algorithm and resulted in the
                 * "Expected server HTML to contain a matching <title> in
                 * <head>" error.
                 *
                 * We can safely leave this element in place on the client
                 * because the same CSS rules are also loaded via the
                 * regular <link> tag that Remix injects through <Links />.
                 * Having them twice does not hurt, and keeps hydration
                 * happy.  If at some point we want to remove the inline
                 * variant after hydration we can do so in an effect, but
                 * for now the duplicate is negligible in size (~2 KB).
                 */}
                <style
                    id="sonner-inline"
                    dangerouslySetInnerHTML={{ __html: sonnerStylesInline }}
                    data-ssr="true"
                    suppressHydrationWarning
                />
            </head>
            <body className="h-screen-safe">
                <ReadOnlyModeBanner {...readOnlyMode} />
                {/* <HydrationDetector /> */}
                <QueryClientProvider client={queryClient}>
                    {/* <ScreenSizeProvider initialScreenSize={initialScreenSize}> */}
                    <HydrationBoundary state={dehydratedState}>
                        <UserAgentContext.Provider value={userAgent}>
                            <SupabaseSessionProvider>
                                <LogoutOverlayProvider>
                                    <AnalyticsProvider>
                                        {/* AIDEV-NOTE: Google Tag Manager with ad-blocker resilience via server-side proxy */}
                                        <GoogleTagManager />
                                        {/* AIDEV-NOTE: Track page/screen views on client navigation */}
                                        <AnalyticsScreenTracker />
                                        {/* AIDEV-NOTE: Track page views in DB for DAU/MAU analytics */}
                                        <PageViewTracker />
                                        {/* AIDEV-NOTE: Monitor and report hydration errors in development */}
                                        <HydrationErrorMonitor />
                                        <LoaderProvider>
                                            <TooltipProvider>
                                                <Toaster />
                                                <Sonner />
                                                <ConfirmDialogProvider>
                                                    <TourProvider
                                                        tours={tourConfigs}
                                                    >
                                                        {/* AIDEV-NOTE: Single global tour overlay+tooltip; page TourRoots own only welcome modals. */}
                                                        <TourViewport />
                                                        {/* AIDEV-NOTE: SurveyProvider handles in-app surveys and single-question popups */}
                                                        <SurveyProvider>
                                                            <Outlet />
                                                            {/* AIDEV-NOTE: PolicyBanner at root level ensures it persists across all routes until consent is given */}
                                                            <PolicyBanner />
                                                            <InstallAppPrompt />
                                                            <PwaLaunchTracker />
                                                            <PwaTitleNormalizer />
                                                            <AppUpdatePrompt />
                                                        </SurveyProvider>
                                                    </TourProvider>
                                                </ConfirmDialogProvider>
                                            </TooltipProvider>
                                        </LoaderProvider>
                                    </AnalyticsProvider>
                                </LogoutOverlayProvider>
                            </SupabaseSessionProvider>
                        </UserAgentContext.Provider>
                    </HydrationBoundary>
                    {/* </ScreenSizeProvider> */}
                </QueryClientProvider>
                {/* AIDEV-NOTE: Wrap scripts in div to avoid hydration errors */}
                <div suppressHydrationWarning>
                    {/* Portal root for LoaderContext - must exist before Scripts */}
                    <div id="loader-portal-root" />
                    <ScrollRestoration />
                    <HashLinkRouter />
                    <Scripts />
                    {process.env.NODE_ENV === "production" && <SpeedInsights />}
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `window.env = ${JSON.stringify(env)}`,
                        }}
                    />
                </div>
            </body>
        </html>
    );
}
